// Modal de DECISÃO pós-análise breve do edital.
// Exibe a síntese da análise do Dwight (Haiku via Bedrock) + 2 caminhos:
//   - Descartar oportunidade (set status_interno='descartado')
//   - Gerar proposta (delega no callback já existente em AbaOportunidades)
//
// Auto-abre quando uma nova análise é detectada (controlado pelo pai); o user
// também pode reabrir manualmente via botão "Ver decisão sugerida" no
// ModalLicitacao.
//
// 2026-05-29 — feature pedida pelo Lucas: "permita que seja revisado o fit e
// sugira a exclusão da oportunidade ou a geração da proposta com um modal".
// 2026-09-02 (passo 3 do plano de licitações) —
//   a) O modal usa a tela (94vw até 900, corpo rola só o que passar) e os
//      botões de decisão saíram do fim do corpo para o RODAPÉ — antes eles
//      rolavam para fora da vista justamente na análise longa, que é a que
//      precisa de decisão.
//   b) 🚨 "Descartar" aqui só trocava o status e a oportunidade CONTINUAVA na
//      lista padrão (o texto prometia que sumia). Agora chama a mesma action
//      da lista, `descartar_oportunidade`, que descarta E arquiva.
//   c) O parse do JSON e o mapa de recomendação viraram `analise.ts`, para o
//      histórico do ModalLicitacao ler pelo mesmo caminho.
import { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Modal, Typography, Space, Tag, Button, Alert, Divider, Card, App as AntApp, Flex,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  RobotOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { Licitacao } from './types';
import { usePerms } from '../../hooks/usePerms';
import {
  parseAnaliseBreve, recomendacaoUI, nivelFitUI, IntentRecomendacao,
} from './analise';

const { Text, Paragraph } = Typography;

interface AnaliseRecord {
  id: number;
  tipo: string;        // 'analise_breve' | 'erro' | 'erro_download'
  conteudo: string;    // JSON string com {recomendacao, fit_tecnico_hydrodata, ...}
  observacoes?: any;
  criado_em: string;
  criado_por?: string;
}

// Ícone que acompanha a recomendação (a parte visual que não cabia no
// helper compartilhado, que é usado também em contexto sem ícone).
function iconeRecomendacao(intent: IntentRecomendacao) {
  if (intent === 'gerar') return <CheckCircleOutlined />;
  if (intent === 'descartar') return <CloseCircleOutlined />;
  return <ExclamationCircleOutlined />;
}

export function ModalDecisao({
  licitacao, analises, onClose, onDescartado, onGerarProposta,
}: {
  licitacao: Licitacao;
  analises: AnaliseRecord[];
  onClose: () => void;
  onDescartado: () => void;
  onGerarProposta: (l: Licitacao) => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [descartando, setDescartando] = useState(false);

  // Pega a análise breve mais recente (analises já vem ORDER BY criado_em DESC).
  const analiseRecente = useMemo(() => {
    return analises.find(a => a.tipo === 'analise_breve' && a.conteudo);
  }, [analises]);

  // Parse defensivo (aceita JSON puro ou envolvido em ```json). Mora em
  // analise.ts porque o histórico do ModalLicitacao lê o mesmo conteúdo.
  const parsed = useMemo(() => parseAnaliseBreve(analiseRecente?.conteudo), [analiseRecente]);

  const recUI = recomendacaoUI(parsed?.recomendacao);
  const fitObj = typeof parsed?.fit_tecnico_hydrodata === 'object' ? parsed.fit_tecnico_hydrodata : null;
  const fitTexto = typeof parsed?.fit_tecnico_hydrodata === 'string' ? parsed.fit_tecnico_hydrodata : null;
  const fitNivelUI = nivelFitUI(fitObj?.nivel);

  // Riscos pode vir como string ou array
  const riscos: string[] = Array.isArray(parsed?.riscos_atencao)
    ? (parsed!.riscos_atencao as string[])
    : (parsed?.riscos_atencao ? [parsed.riscos_atencao as string] : []);

  // `descartar_oportunidade` recusa (404 "já arquivada") quem já saiu da lista
  // — e sai da lista tanto por descarte quanto por "Arquivar vencidas". Nesse
  // caso não há o que arquivar: falta só marcar o status, que é o que a action
  // `atualizar` faz. Sem esta bifurcação, descartar uma oportunidade arquivada
  // pelo robô das vencidas devolvia erro vermelho e não mudava nada.
  const jaArquivada = !!licitacao.arquivada_em;
  // Portão do servidor (05/10/2026): descartar é da triagem (a arquivada vai
  // pelo `atualizar`, régua da ficha); avaliar no pipeline, de quem opera.
  const { pode, erro: permsErro } = usePerms();
  const podeDescartar = permsErro || (jaArquivada
    ? pode('comercial.licitacoes.oportunidades.editar_avaliacao_interna') || pode('comercial.licitacoes.pipeline.operar')
    : pode('comercial.licitacoes.oportunidades.triar'));
  const naTriagem = ['novo', 'em_analise', 'descartado'].includes(licitacao.status_interno);
  const podeAvaliar = permsErro || !naTriagem || pode('comercial.licitacoes.pipeline.operar');

  async function descartar() {
    modal.confirm({
      title: `Descartar "${licitacao.numero_edital || licitacao.id}"?`,
      content: (
        <div style={{ fontSize: 13 }}>
          {jaArquivada ? (
            <>
              Esta oportunidade <strong>já está arquivada</strong> — falta só marcá-la como{' '}
              <strong>Descartado</strong>. Reversível: marque <em>"Mostrar arquivadas"</em> no filtro.
            </>
          ) : (
            <>
              A oportunidade vai para <strong>Descartado</strong> e é <strong>arquivada</strong> —
              sai da lista padrão. Reversível: marque <em>"Mostrar arquivadas"</em> no filtro.
            </>
          )}
        </div>
      ),
      okText: 'Descartar', okButtonProps: { danger: true },
      cancelText: 'Voltar',
      onOk: async () => {
        setDescartando(true);
        try {
          const token = await getToken();
          // 🚨 Era SEMPRE `atualizar` + status_interno='descartado': trocava o
          // status mas NÃO arquivava, então a oportunidade continuava na lista
          // padrão — o oposto do que o texto acima promete.
          // `descartar_oportunidade` é a mesma action do descarte rápido da
          // lista (descarta E arquiva); em quem já está arquivado ela recusa,
          // e aí o `atualizar` continua sendo o caminho certo.
          await axios.post(`${API_URL}/comercial/licitacoes`,
            jaArquivada
              ? { action: 'atualizar', id: licitacao.id, status_interno: 'descartado' }
              : { action: 'descartar_oportunidade', id: licitacao.id },
            { headers: { Authorization: token }, timeout: 15000 });
          message.success('Oportunidade descartada.');
          onDescartado();
          onClose();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        } finally { setDescartando(false); }
      },
    });
  }

  if (!analiseRecente) {
    return (
      <Modal open onCancel={onClose} footer={null} destroyOnHidden
        width="94vw" style={{ maxWidth: 680, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
        title="Decisão sugerida">
        <Alert message="Sem análise disponível" type="info" showIcon
          description="O Dwight ainda não gerou uma análise breve dessa licitação. Dispare em 'Análise breve do edital'." />
      </Modal>
    );
  }

  // Os botões de decisão vivem no RODAPÉ: numa análise longa eles ficavam no
  // fim do corpo e rolavam para fora — o modal pedia decisão sem mostrar como
  // decidir. No rodapé eles ficam sempre à vista, com o corpo rolando atrás.
  const rodape = (
    <Flex justify="space-between" wrap="wrap" gap={8}>
      {podeDescartar ? (
        <Button danger loading={descartando} onClick={descartar}
          icon={<CloseCircleOutlined />}>
          Descartar oportunidade
        </Button>
      ) : <span />}
      <Space>
        <Button onClick={onClose}>Decidir depois</Button>
        {podeAvaliar && (
          <Button type="primary" icon={<CheckCircleOutlined />}
            style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}
            onClick={() => { onClose(); onGerarProposta(licitacao); }}>
            {naTriagem ? 'Avaliar no pipeline' : 'Abrir no pipeline'}
          </Button>
        )}
      </Space>
    </Flex>
  );

  return (
    <Modal open onCancel={onClose} footer={rodape} destroyOnHidden
      width="94vw" style={{ maxWidth: 900, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      title={
        <Space wrap>
          <RobotOutlined style={{ color: recUI.color }} />
          <Text strong style={{ color: '#020D7F' }}>
            Decisão sugerida — {licitacao.numero_edital || `Licitação #${licitacao.id}`}
          </Text>
          <Tag color={recUI.tagColor} style={{ margin: 0 }}>
            {iconeRecomendacao(recUI.intent)} {recUI.label}
          </Tag>
        </Space>
      }>
      <div style={{ paddingTop: 8 }}>
        {/* Banner com a recomendação principal */}
        <Alert
          type={recUI.intent === 'gerar' ? 'success'
              : recUI.intent === 'descartar' ? 'error' : 'warning'}
          message={<strong>{recUI.label}</strong>}
          description={recUI.explicacao}
          showIcon
          style={{ marginBottom: 12 }}
        />

        {/* Fit técnico estruturado */}
        {(fitObj || fitTexto) && (
          <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space>
                <Text strong style={{ fontSize: 13 }}>Fit técnico:</Text>
                {fitObj && <Tag color={fitNivelUI.tagColor}>{fitNivelUI.label}</Tag>}
              </Space>
              <Paragraph style={{ fontSize: 12, marginBottom: 0 }}>
                {fitObj?.justificativa || fitTexto}
              </Paragraph>
            </Space>
          </Card>
        )}

        {/* Resumo executivo (markdown bruto, exibido como texto) */}
        {parsed?.resumo_executivo_md && (
          <Card size="small" title={
            <Space><FileTextOutlined /> <Text strong style={{ fontSize: 12 }}>Resumo executivo</Text></Space>
          } style={{ marginBottom: 12 }}>
            <Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginBottom: 0 }}>
              {parsed.resumo_executivo_md}
            </Paragraph>
          </Card>
        )}

        {/* Riscos */}
        {riscos.length > 0 && (
          <Card size="small" title={
            <Space>
              <ExclamationCircleOutlined style={{ color: '#B91626' }} />
              <Text strong style={{ fontSize: 12 }}>Riscos / atenção</Text>
            </Space>
          } style={{ marginBottom: 12 }}>
            <ul style={{ fontSize: 12, marginBottom: 0, paddingLeft: 18 }}>
              {riscos.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </Card>
        )}

        {/* Dados objetivos (qualif/prazos/local) */}
        {(parsed?.qualificacao_tecnica_resumo
          || parsed?.qualificacao_financeira_resumo
          || parsed?.prazo_execucao
          || parsed?.visita_tecnica) && (
          <Card size="small" title={<Text strong style={{ fontSize: 12 }}>Dados objetivos</Text>}
            style={{ marginBottom: 12 }}>
            <Space direction="vertical" size={4} style={{ width: '100%', fontSize: 12 }}>
              {parsed.prazo_execucao && <div><Text type="secondary">Prazo:</Text> {String(parsed.prazo_execucao)}</div>}
              {/* L15.9 fix: visita_tecnica vem como objeto {obrigatoria, data, observacao} */}
              {parsed.visita_tecnica && (
                <div>
                  <Text type="secondary">Visita técnica:</Text>{' '}
                  {(() => {
                    const v = parsed.visita_tecnica as any;
                    if (typeof v === 'string') return v;
                    const partes: string[] = [];
                    if (v.obrigatoria === true) partes.push('obrigatória');
                    else if (v.obrigatoria === false) partes.push('não obrigatória');
                    if (v.data) partes.push(`data: ${v.data}`);
                    if (v.observacao) partes.push(v.observacao);
                    return partes.length ? partes.join(' · ') : JSON.stringify(v);
                  })()}
                </div>
              )}
              {parsed.qualificacao_tecnica_resumo && (
                <div><Text type="secondary">Qualif. técnica:</Text> {parsed.qualificacao_tecnica_resumo}</div>
              )}
              {parsed.qualificacao_financeira_resumo && (
                <div><Text type="secondary">Qualif. financeira:</Text> {parsed.qualificacao_financeira_resumo}</div>
              )}
            </Space>
          </Card>
        )}

        {/* Fallback quando JSON não parseou. Sem maxHeight/overflow no
            parágrafo: quem rola é o corpo do modal — scroll dentro de scroll
            escondia o fim do texto. */}
        {!parsed && (
          <Alert type="warning" showIcon
            message="Análise não estruturada"
            description={
              <Paragraph style={{ fontSize: 11, whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {analiseRecente.conteudo.slice(0, 2000)}
              </Paragraph>
            }
            style={{ marginBottom: 12 }}
          />
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* Procedência da análise — fecha o corpo; decidir é no rodapé. */}
        <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
          Análise gerada em {new Date(analiseRecente.criado_em).toLocaleString('pt-BR')}
          {analiseRecente.observacoes?.modelo && ` · ${analiseRecente.observacoes.modelo}`}
        </Text>
      </div>
    </Modal>
  );
}
